import { useCallback, useEffect, useMemo, useState } from 'react';

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfWeek = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  const day = result.getDay();
  const diff = (day + 6) % 7;
  result.setDate(result.getDate() - diff);
  return result;
};

const defaultRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentMonday = startOfWeek(today);
  const start = new Date(currentMonday);
  start.setDate(start.getDate() - 7 * 8);
  const end = new Date(currentMonday);
  end.setDate(end.getDate() - 1);
  return {
    startDate: toDateInput(start),
    endDate: toDateInput(end)
  };
};

const EMPTY_CURRENT_WEEK = { start: '', end: '', days: [] };
const EMPTY_DAYPART_WEEKLY = { dayparts: [], weeks: [] };

export const useGamification = ({ authenticated }) => {
  const [filters, setFilters] = useState(() => ({
    ...defaultRange(),
    restaurantName: ''
  }));
  const [summary, setSummary] = useState(null);
  const [rankings, setRankings] = useState({ top: [], bottom: [] });
  const [levels, setLevels] = useState([]);
  const [latestSync, setLatestSync] = useState(null);
  const [restaurantCount, setRestaurantCount] = useState(0);
  const [weeks, setWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(EMPTY_CURRENT_WEEK);
  const [daypartWeekly, setDaypartWeekly] = useState(EMPTY_DAYPART_WEEKLY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!authenticated) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
        weeks: '8'
      });
      if (filters.restaurantName) {
        params.append('restaurant_name', filters.restaurantName);
      }
      const response = await fetch(`/api/gamification/overview?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudo cargar la gamificación.');
      }
      setSummary(data.summary || null);
      setLevels(data.levels || []);
      setLatestSync(data.latest_sync || null);
      setRestaurantCount(data.restaurant_count || 0);
      setWeeks(Array.isArray(data.weeks) ? data.weeks : []);
      setCurrentWeek(data.current_week || EMPTY_CURRENT_WEEK);
      setDaypartWeekly(data.daypart_weekly || EMPTY_DAYPART_WEEKLY);
      setRankings({
        top: data.rankings?.top || [],
        bottom: data.rankings?.bottom || []
      });
    } catch (err) {
      setError(err?.message || 'Error al cargar gamificación.');
      setSummary(null);
      setRankings({ top: [], bottom: [] });
      setWeeks([]);
      setCurrentWeek(EMPTY_CURRENT_WEEK);
      setDaypartWeekly(EMPTY_DAYPART_WEEKLY);
    } finally {
      setLoading(false);
    }
  }, [authenticated, filters.startDate, filters.endDate, filters.restaurantName]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilterValue = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  return useMemo(() => ({
    filters,
    summary,
    rankings,
    levels,
    latestSync,
    restaurantCount,
    weeks,
    currentWeek,
    daypartWeekly,
    loading,
    error,
    setFilterValue,
    reload: load
  }), [filters, summary, rankings, levels, latestSync, restaurantCount, weeks, currentWeek, daypartWeekly, loading, error, setFilterValue, load]);
};
